import React, { useMemo } from "react";

import useTimeState from "hooks/dashboard/useTimeState";
import { LocalDateTime, ZoneId } from "@js-joda/core";
import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { T } from "services/lang/LanguageService";
import { isNumeric } from "utils/utils";
import SparkLineHandler from "./SparkLineHandler";
import { AdvancedProps, SparkLineProps, Usages } from "./SparkLineTypes";
import { defaultFromDateTime, defaultToDateTime } from "../charts/utils";

export default function SparkLine(props: SparkLineProps) {
  const valueScaling = props.valueScaling || 1;
  const count = props.count || 14;
  const dataset = isNumeric(props.dataset)
    ? `${props.dataset}:CATEGORIZED_DATASET`
    : (props.dataset as string);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTime({
      loadConf: {
        alignUnit: "MINUTES",
        lag: 6,
        lagUnit: "HOURS",
        lead: 0,
        leadUnit: "HOURS",
        ...props.loadConf,
      },
      referenceTime: props.referenceTime,
    });

  const { data } = useGetRecentData([dataset], {
    count,
    refTime:
      props.referenceTime &&
      LocalDateTime.parse(props.referenceTime).atZone(ZoneId.systemDefault()).toString(),
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset && !props.useLoadConf,
    },
  });

  const { data: dataRange } = useGetDataInfoRange(
    [dataset],
    {
      timeStart: defaultFromDate?.toISOString(),
      timeEnd: defaultToDate?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: !!props.dataset && props.useLoadConf && !!defaultFromDate && !!defaultToDate,
    }
  );

  const sortedData = useMemo(() => {
    if (props.useLoadConf) {
      return dataRange?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
    }
    return data?.[0].data.sort((a, b) => a.key.localeCompare(b.key)) || [];
  }, [data, dataRange, props.useLoadConf]);

  const transformedData = useMemo((): {
    dataArray: number[];
    lastValueTime: null | Date;
    unit?: string;
    decimalPlaces?: number;
    title?: string;
  } => {
    if (!props.useLoadConf && !data?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };
    if (props.useLoadConf && !dataRange?.[0]?.data?.length)
      return { dataArray: [], lastValueTime: null };

    const values = sortedData.map(d => d.value * valueScaling);
    const lastValueTime = new Date(sortedData[sortedData.length - 1].key);
    const unit = props.unit ?? data?.[0]?.unit ?? dataRange?.[0]?.unit ?? "";
    const decimalPlaces = props.decimalPlaces ?? data?.[0]?.decimalPlaces ?? dataRange?.[0]?.decimalPlaces;
    const title = data?.[0]?.title ?? dataRange?.[0]?.title;

    return { dataArray: values, lastValueTime, unit, decimalPlaces, title };
  }, [data, dataRange, sortedData, valueScaling, props.useLoadConf, props.decimalPlaces]);

  const formattedData = useMemo(() => {
    return sortedData.map(d => ({ y: d.value * valueScaling, x: new Date(d.key) }));
  }, [sortedData, valueScaling]);

  // props from legacy config
  const legacyProps = {
    // @ts-expect-error
    constant: props.constant,
    // @ts-expect-error
    dataset: props.baselineDataset,
    // @ts-expect-error
    usage: (props.useConstant ? "constant" : "dataset") as Usages,
    // @ts-expect-error
    color: props.baselineColor,
    // @ts-expect-error
    width: props.baselineWidth,
    // @ts-expect-error
    fillAboveColor: props.fillAboveColor,
    // @ts-expect-error
    fillBelowColor: props.fillBelowColor,
  };

  const advancedProps: AdvancedProps = {
    baseline: {
      ...legacyProps,
      ...props.baseline,
    },
    data: formattedData,
    minLine: props.minLine,
    maxLine: props.maxLine,
    count,
    updateFrequency: props.updateFrequency,
    referenceTime: props.referenceTime,
    showYAxis: props.showYAxis,
    fromDate: defaultFromDate,
    toDate: defaultToDate,
  };

  return (!props.useLoadConf && data?.length) || (props.useLoadConf && dataRange?.length) ? (
    <SparkLineHandler
      dataArray={transformedData.dataArray}
      chartIndex={props.chartIndex}
      lastValueTime={transformedData.lastValueTime}
      decimalPlaces={transformedData.decimalPlaces}
      labelFontSize={props.labelFontSize}
      sparklineWidth={props.sparkLineWidth}
      lineColor={props.lineColor}
      lineWidth={props.lineWidth}
      unit={transformedData.unit}
      maxAge={props.maxAge}
      dataset={dataset}
      title={transformedData.title ?? ""}
      height={props.height}
      overlayLabel={props.overlayLabel}
      vertical={props.vertical}
      horizontal={props.horizontal}
      advanced={props.advanced}
      advancedProps={advancedProps}
      generalProperties={props.generalProperties}
      isMultipleCharts={props.isMultipleCharts}
      currentBiggestValueWidth={props.currentBiggestValueWidth}
      setCurrentBiggestValueWidth={props.setCurrentBiggestValueWidth}
      stringValueMapper={data?.[0].stringValueMapper ?? dataRange?.[0].stringValueMapper}
      displayCharts={props.displayCharts}
      minYAxis={props.minYAxis}
      maxYAxis={props.maxYAxis}
    />
  ) : (
    <div style={{ height: `${props.height ?? "auto"}px` }}>{T("word.no-records")}</div>
  );
}
